/** @jsxImportSource theme-ui @jsxRuntime classic */
import { H } from '@hzdg/sectioning';
import React, { Fragment } from 'react';
import {
  formatPhoneNumberAsLink,
  Link,
  ArrowLink,
  Box,
  Text,
  SxStyleProp,
  useBreakpointIndex,
} from '@tishman/components';

export interface ContactsInfoProps {
  /** info type, currently we support email and phone */
  type: string;
  /** info value, currently either email address or phone number */
  value: string;
}

export interface HoursAndAddressProps {
  /** Title above hours block */
  hoursHeading?: string;
  /** Array of hours text,
   * use getHoursProps() to format sanity hours data  */
  hours?: readonly string[];
  /**
   * Title above address block.
   *
   * if not provided, hours block will not be rendered */
  addressHeading?: string;
  /** location object
   *
   * if both location and contactsInfo are not provided,
   * address block will not be rendered
   */
  location?: {
    /** google map url  */
    url?: string;
    /** location title  */
    title?: string;
    /** street address */
    address1?: string;
    /** city, state, zip address */
    address2?: string;
  };
  /**
   * contactsInfo is a sanity object
   * which we loop through and add phone and email links
   *
   * if both location and contactsInfo are not provided,
   * address block will not be rendered
   *  */
  contactsInfo?: readonly ContactsInfoProps[];
  /** The label for the contactUs link */
  contactLinkLabel?: string;
  /** The url for the contactUs link */
  contactLinkUrl?: string;
  closed?: boolean;
  row?: boolean;
}

const contactLinkHref = (type: string, value: string) => {
  if (type == 'email') {
    return `mailto:${value}`;
  } else if (type == 'phone') {
    return formatPhoneNumberAsLink(value);
  } else {
    return value;
  }
};

export const HolidayHoursAndAddress = ({
  hoursHeading = 'Hours',
  hours,
  addressHeading = 'Directions & Contact',
  location,
  contactsInfo,
  contactLinkLabel = 'Contact Us',
  contactLinkUrl,
  closed = false,
  row = false,
}: HoursAndAddressProps): JSX.Element => {
  const isMobile = useBreakpointIndex() < 2;

  const linkStyles: SxStyleProp = {
    'variant': 'text.hoursAndDirections',
    'display': 'block',
    'textDecoration': 'none',
    '&:hover': {
      textDecoration: 'underline',
      color: 'inherit',
    },
  };

  const addressElement = location && (
    <Fragment>
      {location.address1 && (
        <Text variant="hoursAndDirections">{location.address1}</Text>
      )}
      {location.address2 && (
        <Text variant="hoursAndDirections">{location.address2}</Text>
      )}
    </Fragment>
  );

  return (
    <Box
      data-hours-address
      data-module="HoursAndAddress"
      sx={{
        flexShrink: 0,
        maxWidth: 550,
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 4,
      }}
    >
      {hours && (
        <Box
          sx={{
            mb: 4,
            flex: 1,
          }}
        >
          <H
            sx={{
              variant: 'styles.h3',
              fontFamily: 'body',
              mb: 2,
            }}
          >
            {hoursHeading}
          </H>

          {hours.map((text) => (
            <Text key={text} variant="hoursAndDirections">
              {text}
            </Text>
          ))}
        </Box>
      )}
      {(location || contactsInfo) && (
        <Box
          sx={{
            mb: 4,
            flex: 1,
          }}
        >
          <H
            sx={{
              variant: 'styles.h3',
              fontFamily: 'body',
              mb: 2,
            }}
          >
            {addressHeading}
          </H>

          {location?.url ? (
            <Link href={location.url} sx={linkStyles} target="_blank">
              {addressElement}
            </Link>
          ) : (
            addressElement
          )}

          {contactsInfo &&
            contactsInfo.map((contact) => (
              <Link
                href={contactLinkHref(contact.type, contact.value)}
                key={contact.value}
                sx={linkStyles}
                target={contact.type === 'link' ? '_blank' : '_self'}
              >
                {contact.value}
              </Link>
            ))}

          {contactLinkUrl && (
            <Box>
              <ArrowLink
                href={contactLinkUrl}
                label={contactLinkLabel}
                sx={{
                  width: 'auto',
                  display: 'inline-flex',
                  marginTop: isMobile ? 4 : 0,
                }}
                underline
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
